import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";
import ClaimFormSparteCostTypes from "./ClaimFormSparteCostTypes.jsx";
import ClaimFormSparteClaimCauses from "./ClaimFormSparteClaimCauses.jsx";
import ClaimFormSparteDialogHeading from "./ClaimFormSparteDialogHeading.jsx";

const sameList = (a, b) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Edits the settings of one Sparte locally; nothing is sent until the
 * parent's onSave receives the changed settings.
 */
export default function ClaimFormSparteEditDialog({
  sparte,
  coverageTypes,
  definition,
  causes,
  isSaving,
  onSave,
  onClose,
}) {
  const [draft, setDraft] = useState({
    coverage_type: sparte.coverage_type ?? "",
    cost_types: sparte.cost_types ?? null,
    claim_causes: sparte.claim_causes ?? null,
  });
  const hasCoverageTypes = coverageTypes.length > 0;
  const draftSparte = {
    ...sparte,
    ...draft,
    coverage_type: draft.coverage_type || null,
  };
  const patch = (changes) =>
    setDraft((current) => ({ ...current, ...changes }));

  const changes = {};
  if (
    hasCoverageTypes &&
    draft.coverage_type &&
    draft.coverage_type !== (sparte.coverage_type ?? "")
  ) {
    changes.coverage_type = draft.coverage_type;
  }
  if (!sameList(draft.cost_types, sparte.cost_types)) {
    changes.cost_types = draft.cost_types;
  }
  if (!sameList(draft.claim_causes, sparte.claim_causes)) {
    changes.claim_causes = draft.claim_causes;
  }

  const ownCauseCount = draft.claim_causes
    ? causes.filter((cause) => draft.claim_causes.includes(cause.cause_id))
        .length
    : null;
  const isValid = draft.cost_types?.length !== 0 && ownCauseCount !== 0;
  const canSave = Object.keys(changes).length > 0 && isValid && !isSaving;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{sparte.name} bearbeiten</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} pt={1}>
          {hasCoverageTypes && (
            <>
              <Box>
                <ClaimFormSparteDialogHeading description="Bestimmt, unter welcher Frage bei „Was ist passiert?“ die Sparte erscheint und in welchem Formularschritt ihre Angaben und Kosten erfasst werden.">
                  Deckungsart
                </ClaimFormSparteDialogHeading>
                <TextField
                  select
                  size="small"
                  value={draft.coverage_type}
                  onChange={(event) =>
                    patch({ coverage_type: event.target.value })
                  }
                  disabled={isSaving}
                  slotProps={{
                    select: { native: true },
                    htmlInput: { "aria-label": "Deckungsart" },
                  }}
                  sx={{ minWidth: 240 }}
                >
                  <option value="" disabled>
                    Bitte wählen
                  </option>
                  {coverageTypes.map((coverageType) => (
                    <option key={coverageType.key} value={coverageType.key}>
                      {coverageType.label}
                    </option>
                  ))}
                </TextField>
              </Box>
              <Divider />
            </>
          )}
          <Box>
            <ClaimFormSparteDialogHeading description="Welche Kostenpositionen sollen für diese Sparte angelegt werden können?">
              Kostenarten
            </ClaimFormSparteDialogHeading>
            <ClaimFormSparteCostTypes
              sparte={draftSparte}
              definition={definition}
              disabled={isSaving}
              onChange={(cost_types) => patch({ cost_types })}
            />
            {draft.cost_types?.length === 0 && (
              <Typography variant="caption" color="error">
                Mindestens eine Kostenart auswählen
              </Typography>
            )}
          </Box>
          <Divider />
          <Box>
            <ClaimFormSparteDialogHeading description="Welche Ursachen sollen bei Schäden dieser Sparte auswählbar sein?">
              Ursachen
            </ClaimFormSparteDialogHeading>
            <ClaimFormSparteClaimCauses
              sparte={draftSparte}
              causes={causes}
              disabled={isSaving}
              onChange={(claim_causes) => patch({ claim_causes })}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="tertiary" onClick={onClose} disabled={isSaving}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={() => onSave(changes)}
          disabled={!canSave}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ClaimFormSparteEditDialog.propTypes = {
  sparte: PropTypes.object.isRequired,
  coverageTypes: PropTypes.array.isRequired,
  definition: PropTypes.object,
  causes: PropTypes.array.isRequired,
  isSaving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
